import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Badge } from "../ui/badge";
import { Warning } from "@phosphor-icons/react";

const ms = (v) => (v == null ? "—" : `${Math.round(v)} ms`);

export function TenantTraffic({ range, tick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    API.get("/platform/system-status/tenants", { params: { range } }).then(({ data }) => alive && setData(data)).catch(() => {});
    return () => { alive = false; };
  }, [range, tick]);
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="tenant-traffic">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-sm font-semibold">Traffic by workspace</h3>
        {data && (
          <span className="text-[11px] text-muted-foreground" data-testid="tenant-traffic-summary">
            {data.total_requests.toLocaleString()} requests in this range
            {data.noisy.length > 0 && <span className="ml-2 text-amber-700 dark:text-amber-400 font-medium" data-testid="tenant-traffic-noisy-count">{data.noisy.length} noisy</span>}
          </span>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">A workspace is flagged noisy when it makes over {data?.thresholds?.share_pct ?? 40}% of all requests or its own server-error rate passes {data?.thresholds?.error_pct ?? 5}% (with at least {data?.thresholds?.min_requests ?? 20} requests).
        <span className="block mt-0.5" data-testid="tenant-alert-policy">Platform admins are alerted (same quiet rules: once per episode, 6 h apart) when a workspace does this for 10 minutes with real volume — 200+ requests platform-wide and 100+ of its own at 3× its usual rate, or 50+ requests with over 5% server errors.</span></p>
      {!data ? <p className="text-xs text-muted-foreground">Loading…</p> : data.rows.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="tenant-traffic-empty">No requests recorded in this range.</p> : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 font-medium">Workspace</th>
              <th className="py-1 pr-2 font-medium w-[28%]">Share</th>
              <th className="py-1 pr-2 font-medium text-right">Requests</th>
              <th className="py-1 pr-2 font-medium text-right">5xx</th>
              <th className="py-1 pr-2 font-medium text-right">4xx</th>
              <th className="py-1 pr-2 font-medium text-right">Avg</th>
              <th className="py-1 font-medium text-right">Max</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.tenant_id} className={`border-t border-border ${r.noisy ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`} data-testid={`tenant-traffic-row-${r.slug || r.tenant_id}`}>
                <td className="py-1.5 pr-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {r.noisy && <Warning size={12} weight="fill" className="text-amber-600 shrink-0" />}
                    <span className={`truncate ${r.special ? "text-muted-foreground italic" : "font-medium"}`}>{r.name}</span>
                    {r.plan && <Badge variant="outline" className="text-[9px] capitalize">{r.plan}</Badge>}
                    {r.noisy && <Badge className="text-[9px] bg-amber-100 text-amber-800" data-testid={`tenant-traffic-noisy-${r.slug || r.tenant_id}`}>noisy · {r.noisy_reason}</Badge>}
                  </div>
                </td>
                <td className="py-1.5 pr-2">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded bg-muted overflow-hidden"><div className={`h-full ${r.noisy ? "bg-amber-500" : "bg-sky-500"}`} style={{ width: `${Math.min(100, r.share_pct)}%` }} /></div>
                    <span className="tabular-nums w-10 text-right">{r.share_pct}%</span>
                  </div>
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{r.requests.toLocaleString()}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${r.err5 ? "text-red-600 font-semibold" : ""}`}>{r.err5}{r.err5 ? ` (${r.error_rate}%)` : ""}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{r.err4}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{ms(r.avg_ms)}</td>
                <td className="py-1.5 text-right tabular-nums">{ms(r.max_ms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
